import { useQuery } from '@tanstack/react-query';
import { getCallLog, isElasticSearchEnabled } from '@/services/call-log-service/call-log-service';
import { CallLogFilters } from '@/services/call-log-service/call-log-service.types';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { MAX_ROWS } from '@/services/call-log-service/call-log-service.constants';

export type RatedSample = {
  scores: (number | null)[];
  sampled: number;
  atCeiling: boolean;
  /** False while the gate withholds the query: a sample of nought is not an empty window. */
  arrived: boolean;
  isLoading: boolean;
  error: unknown;
};

/** The sms_score column of one capped page of calls, for a distribution counted here. */
export function useRatedSample(filters: CallLogFilters, canQuery: boolean): RatedSample {
  const params = {
    ...filters,
    // Voice calls only, to match what the headline aggregate covers for a standard tenant.
    type: 'AnalyticsCall',
    // The caption promises the most recent calls, so the column is pinned here rather
    // than left to the endpoint's default.
    sort_by: 'created_at',
    order_by: 'desc' as const,
    page: 1,
    items_per_page: MAX_ROWS,
  };

  // The gate useInsights runs on this screen. Anything narrower and the two disagree: the
  // headline loads while the distribution reports an empty range it never asked about.
  const enabled = canQuery || !!filters.company_name;

  const { data, isLoading, error } = useQuery({
    // The backend decides both the URL and the response transformer, so it belongs in the
    // key: only the search path annotates the count as a lower bound.
    queryKey: [...ReactQueryKeys.CALL_LOG(params), isElasticSearchEnabled() ? 'es' : 'pg'],
    queryFn: () => getCallLog(params),
    staleTime: Infinity,
    enabled,
  });

  const calls = data?.calls ?? [];

  return {
    scores: calls.map((call) => call.sms_score),
    sampled: calls.length,
    arrived: Boolean(data),
    // A lower-bound count means the server stopped counting, so treat it as capped.
    atCeiling: data?.calls_count_relation === 'gte' || (data?.calls_count ?? 0) > calls.length,
    isLoading,
    error,
  };
}
